import React from "react";
import Popup from "reactjs-popup";
import type {
  DriverColumnFacets,
  DriverDutyFacet,
  DriverGroupSortKey,
  ProgressFacet,
  SortDirection,
} from "../hooks/driverGroupSortFilter";
import { facetsAreActive } from "../hooks/driverGroupSortFilter";
import "./driversColumnToolbar.scss";

const DUTY_OPTIONS: { value: DriverDutyFacet; label: string }[] = [
  { value: "AVAILABLE", label: "Available" },
  { value: "ON_DUTY", label: "On duty" },
  { value: "OFF_DUTY", label: "Off duty" },
];

const PROGRESS_OPTIONS: { value: ProgressFacet; label: string }[] = [
  { value: "none", label: "None" },
  { value: "has_remaining", label: "Has remaining" },
  { value: "all_done", label: "All done" },
];

const SORT_OPTIONS: { value: DriverGroupSortKey; label: string }[] = [
  { value: "remaining_work", label: "Remaining work" },
  { value: "name", label: "Driver name" },
  { value: "duty", label: "Duty status" },
  { value: "delivery_ratio", label: "Delivery completion" },
  { value: "pickup_ratio", label: "Pickup completion" },
];

function toggleFacet<T extends string>(
  current: T[],
  value: T,
  onChange: (next: T[]) => void
): void {
  if (current.includes(value)) {
    onChange(current.filter((v) => v !== value));
  } else {
    onChange([...current, value]);
  }
}

export interface DriversColumnToolbarProps {
  sortKey: DriverGroupSortKey;
  sortDirection: SortDirection;
  onSortKeyChange: (key: DriverGroupSortKey) => void;
  onSortDirectionChange: (dir: SortDirection) => void;
  facets: DriverColumnFacets;
  onFacetsChange: (facets: DriverColumnFacets) => void;
  /** False when sort/facets match product defaults (name A–Z, on-duty only). */
  matchesDefaults: boolean;
  onResetToolbar: () => void;
}

export const DriversColumnToolbar: React.FC<DriversColumnToolbarProps> = ({
  sortKey,
  sortDirection,
  onSortKeyChange,
  onSortDirectionChange,
  facets,
  onFacetsChange,
  matchesDefaults,
  onResetToolbar,
}) => {
  return (
    <div className="xgs-drivers-column-toolbar">
      <div className="xgs-drivers-column-toolbar__row">
        <label className="xgs-drivers-column-toolbar__label" htmlFor="drivers-sort-key">
          Sort
        </label>
        <select
          id="drivers-sort-key"
          className="xgs-drivers-column-toolbar__select"
          value={sortKey}
          onChange={(e) => onSortKeyChange(e.target.value as DriverGroupSortKey)}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="xgs-drivers-column-toolbar__sr-only" htmlFor="drivers-sort-dir">
          Sort direction
        </label>
        <select
          id="drivers-sort-dir"
          className="xgs-drivers-column-toolbar__select xgs-drivers-column-toolbar__select--narrow"
          value={sortDirection}
          onChange={(e) => onSortDirectionChange(e.target.value as SortDirection)}
          aria-label="Sort direction"
        >
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </select>
        <Popup
          trigger={
            <button
              type="button"
              className="xgs-drivers-column-toolbar__filter-btn"
              aria-haspopup="dialog"
              title={
                facetsAreActive(facets)
                  ? "Filters: duty, delivery, and/or pickup selections are on"
                  : "Filters: duty, delivery, and pickup workload"
              }
            >
              Filters{!matchesDefaults ? " · active" : ""}
            </button>
          }
          position="bottom left"
          closeOnDocumentClick
          on={["click"]}
          contentStyle={{ padding: 0, border: "none", zIndex: 1200 }}
        >
          {(close: () => void) => (
            <div
              className="xgs-drivers-column-toolbar__popover"
              role="dialog"
              aria-label="Driver filters"
            >
              <div className="xgs-drivers-column-toolbar__popover-section">
                <div className="xgs-drivers-column-toolbar__popover-title">Duty</div>
                <div className="xgs-drivers-column-toolbar__chips">
                  {DUTY_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={
                        facets.duty.includes(o.value)
                          ? "xgs-drivers-column-toolbar__chip xgs-drivers-column-toolbar__chip--on"
                          : "xgs-drivers-column-toolbar__chip"
                      }
                      onClick={() =>
                        toggleFacet(facets.duty, o.value, (duty) =>
                          onFacetsChange({ ...facets, duty })
                        )
                      }
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="xgs-drivers-column-toolbar__popover-section">
                <div className="xgs-drivers-column-toolbar__popover-title">Deliveries</div>
                <div className="xgs-drivers-column-toolbar__chips">
                  {PROGRESS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={
                        facets.deliveries.includes(o.value)
                          ? "xgs-drivers-column-toolbar__chip xgs-drivers-column-toolbar__chip--on"
                          : "xgs-drivers-column-toolbar__chip"
                      }
                      onClick={() =>
                        toggleFacet(facets.deliveries, o.value, (deliveries) =>
                          onFacetsChange({ ...facets, deliveries })
                        )
                      }
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="xgs-drivers-column-toolbar__popover-section">
                <div className="xgs-drivers-column-toolbar__popover-title">Pickups</div>
                <div className="xgs-drivers-column-toolbar__chips">
                  {PROGRESS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={
                        facets.pickups.includes(o.value)
                          ? "xgs-drivers-column-toolbar__chip xgs-drivers-column-toolbar__chip--on"
                          : "xgs-drivers-column-toolbar__chip"
                      }
                      onClick={() =>
                        toggleFacet(facets.pickups, o.value, (pickups) =>
                          onFacetsChange({ ...facets, pickups })
                        )
                      }
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="xgs-drivers-column-toolbar__popover-actions">
                <button
                  type="button"
                  className="xgs-drivers-column-toolbar__link-btn"
                  onClick={() => {
                    onResetToolbar();
                    close();
                  }}
                >
                  Reset to defaults
                </button>
                <button
                  type="button"
                  className="xgs-drivers-column-toolbar__primary-btn"
                  onClick={() => close()}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </Popup>
        {!matchesDefaults && (
          <button
            type="button"
            className="xgs-drivers-column-toolbar__link-btn xgs-drivers-column-toolbar__clear-inline"
            onClick={onResetToolbar}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};
